/**
 * 远程产品图缓存（按 brand + modelKey）。
 * HTTP 拉取在 main-app；本模块只存结果，供 useProductColor / 各应用共读。
 *
 * qiankun 下主/子应用各自打包 shared-store，模块级 Map 不共享；
 * 因此缓存、订阅与 loader 一律挂在 window 单例上。
 */

import type { ColorOption } from "./useProductColor";

/**
 * 无图 / 拉取失败时的占位标记（空串）。
 * UI 应显示文案「暂未上传产品图」，不要渲染灰色背景图。
 */
export const PRODUCT_PIC_PLACEHOLDER = "";

/** 是否为「未上传 / 无有效产品图」 */
export function isProductPicMissing(src: string | null | undefined): boolean {
  return !src || src === PRODUCT_PIC_PLACEHOLDER;
}

export type RemoteProductPicColor = ColorOption & {
  swatchType?: "solid" | "gradient" | string;
  swatchValue?: string;
  isDefault?: boolean;
  sortOrder?: number;
};

export type RemoteProductPicEntry = {
  brand: string;
  modelKey: string;
  defaultColorKey?: string;
  /** epoch ms */
  expiresAt: number;
  colors: RemoteProductPicColor[];
  /** ok=有数据 empty=接口空 error=请求失败 */
  status: "ok" | "empty" | "error";
};

type Listener = () => void;

export type ProductPicLoader = {
  ensure: (
    brand: string,
    modelKey: string,
    options?: { force?: boolean }
  ) => Promise<unknown>;
  getBrand: () => string;
};

type RemoteProductPicShared = {
  cache: Map<string, RemoteProductPicEntry>;
  listeners: Set<Listener>;
  version: number;
  loader: ProductPicLoader | null;
};

const WINDOW_KEY = "__EWEADN_REMOTE_PRODUCT_PICS__";

function getShared(): RemoteProductPicShared {
  if (typeof window === "undefined") {
    // SSR / 非浏览器：退回进程内临时态（各调用方独立，仅防崩）
    return {
      cache: new Map(),
      listeners: new Set(),
      version: 0,
      loader: null,
    };
  }
  const w = window as unknown as Record<
    string,
    RemoteProductPicShared | undefined
  >;
  if (!w[WINDOW_KEY]) {
    w[WINDOW_KEY] = {
      cache: new Map(),
      listeners: new Set(),
      version: 0,
      loader: null,
    };
  }
  return w[WINDOW_KEY]!;
}

/** 供 React 订阅：远程缓存变更时递增 */
export function getRemoteProductPicVersion(): number {
  return getShared().version;
}

/** 去掉实例后缀 / moudleId 作用域，得到接口用的型号键 */
export function normalizeProductPicModelKey(modelKey: string): string {
  let k = (modelKey || "").trim();
  if (!k) return "";
  const inst = k.indexOf("@@");
  if (inst !== -1) k = k.slice(0, inst);
  const mid = k.indexOf(":moudleId:");
  if (mid !== -1) k = k.slice(0, mid);
  if (k.startsWith("EWEADN ")) k = k.slice(7).trim();
  return k;
}

function cacheKey(brand: string, modelKey: string): string {
  return `${(brand || "eweadn").trim().toLowerCase()}::${normalizeProductPicModelKey(modelKey)}`;
}

export function remoteProductPicCacheKey(
  brand: string,
  modelKey: string
): string {
  return cacheKey(brand, modelKey);
}

export function subscribeRemoteProductPics(listener: Listener): () => void {
  const shared = getShared();
  shared.listeners.add(listener);
  return () => shared.listeners.delete(listener);
}

function notify() {
  const shared = getShared();
  shared.version += 1;
  shared.listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(new CustomEvent("eweadn:remoteProductPicChange"));
    } catch {
      /* ignore */
    }
  }
}

export function setRemoteProductPic(entry: RemoteProductPicEntry): void {
  if (!entry?.modelKey) return;
  const normalized: RemoteProductPicEntry = {
    ...entry,
    modelKey: normalizeProductPicModelKey(entry.modelKey) || entry.modelKey,
  };
  getShared().cache.set(
    cacheKey(normalized.brand, normalized.modelKey),
    normalized
  );
  notify();
}

export function getRemoteProductPic(
  brand: string,
  modelKey: string
): RemoteProductPicEntry | undefined {
  if (!modelKey) return undefined;
  return getShared().cache.get(cacheKey(brand, modelKey));
}

/** 按型号键查找（任意品牌，优先精确 brand） */
export function findRemoteProductPicByModelKey(
  modelKey: string,
  preferredBrand?: string
): RemoteProductPicEntry | undefined {
  const key = normalizeProductPicModelKey(modelKey);
  if (!key) return undefined;
  if (preferredBrand) {
    const hit = getRemoteProductPic(preferredBrand, key);
    if (hit) return hit;
  }
  for (const entry of getShared().cache.values()) {
    if (entry.modelKey === key) return entry;
  }
  return undefined;
}

export function isRemoteProductPicExpired(
  entry: RemoteProductPicEntry | undefined
): boolean {
  if (!entry) return true;
  // 提前 5 分钟视为过期，避免展示瞬间 403
  return Date.now() >= entry.expiresAt - 5 * 60 * 1000;
}

export function clearRemoteProductPic(brand: string, modelKey: string): void {
  getShared().cache.delete(cacheKey(brand, modelKey));
  notify();
}

/** 主应用注册；子应用 ProductImage / DeviceStatus 经 window 单例调用 */
export function registerProductPicLoader(loader: ProductPicLoader): void {
  getShared().loader = loader;
}

export function getProductPicLoader(): ProductPicLoader | null {
  if (typeof window === "undefined") return null;
  return getShared().loader;
}

/** 按需拉取；未注册 loader 时静默跳过。缓存非 ok 时强制重拉（避免刚入库仍命中 empty） */
export function requestProductPic(modelKey: string, brand?: string): void {
  const key = normalizeProductPicModelKey(modelKey);
  if (!key) return;
  const loader = getProductPicLoader();
  if (!loader) return;
  const b =
    (brand || loader.getBrand() || "eweadn").trim().toLowerCase() || "eweadn";
  const cached = getRemoteProductPic(b, key);
  const force = !cached || cached.status !== "ok";
  void loader.ensure(b, key, force ? { force: true } : undefined);
}
